import { type IconDefinition, faBan, faBuildingFlag, faCrown, faStore } from '@fortawesome/pro-solid-svg-icons';

interface FlatBaseFee {
    kind: 'flat';
    key: string;
    label: string;
    amountField: string;
    routingField?: string;
    routingDescription?: string;
}

interface QuantityBaseFee {
    kind: 'quantity';
    key: string;
    label: string;
    quantityField: string;
    rateField: string;
    routingField?: string;
    routingDescription?: string;
}

export type BaseFee = FlatBaseFee | QuantityBaseFee;

export interface BaseFeeRoutingOption {
    value: string;
    label: string;
}

interface PayerStyle {
    icon: IconDefinition;
    background: string;
    color: string;
}

export type PricingValues = Readonly<Record<string, unknown>>;

export const TEAM_BASE_FEES: readonly BaseFee[] = [
    { kind: 'flat', key: 'license_fee', label: 'License Fee', amountField: 'license_fee' },
    {
        kind: 'flat',
        key: 'professional_services_fee',
        label: 'Professional Services',
        amountField: 'professional_services_fee',
    },
    {
        kind: 'quantity',
        key: 'additional_seats',
        label: 'Additional Seats',
        quantityField: 'additional_seats_quantity',
        rateField: 'additional_seats_rate',
    },
    {
        kind: 'quantity',
        key: 'location_fee',
        label: 'Location Fee',
        quantityField: 'location_fee_quantity',
        rateField: 'location_fee_rate',
    },
    { kind: 'flat', key: 'rnd_lookup_fee', label: 'RND Lookup Fee', amountField: 'rnd_lookup_fee' },
];

export const ROUTED_BASE_FEES: readonly BaseFee[] = [
    {
        kind: 'flat',
        key: 'license_fee',
        label: 'License Fee',
        amountField: 'license_fee',
        routingField: 'license_fee_routing',
        routingDescription: 'Determines which account is charged for this license fee.',
    },
    {
        kind: 'flat',
        key: 'professional_services_fee',
        label: 'Professional Services',
        amountField: 'professional_services_fee',
        routingField: 'professional_services_fee_routing',
        routingDescription: 'Determines which account is charged for professional services.',
    },
    {
        kind: 'quantity',
        key: 'location_fee',
        label: 'Location Fee',
        quantityField: 'location_fee_quantity',
        rateField: 'location_fee_rate',
        routingField: 'location_fee_routing',
        routingDescription: 'Determines which account is charged for location fees.',
    },
];

export const ORG_UNIT_ROUTING_OPTIONS: readonly BaseFeeRoutingOption[] = [
    { value: 'org_unit', label: 'OU Pays' },
    { value: 'corporate', label: 'Corporate Pays' },
];

export const GROUP_ROUTING_OPTIONS: readonly BaseFeeRoutingOption[] = [
    { value: 'group', label: 'Group Pays' },
    { value: 'org_unit', label: 'OU Pays' },
    { value: 'corporate', label: 'Corporate Pays' },
];

const baseFeeFields = (fee: BaseFee): string[] =>
    'flat' === fee.kind ? [fee.amountField] : [fee.quantityField, fee.rateField];

export const isBaseFeeInUse = (fee: BaseFee, values: PricingValues): boolean =>
    baseFeeFields(fee).every((field) => Number(values[field]) > 0);

export const validateListedBaseFees = (
    fees: readonly BaseFee[],
    listed: readonly string[],
    values: PricingValues
): Record<string, string> => {
    const errors: Record<string, string> = {};

    for (const fee of fees) {
        if (!listed.includes(fee.key)) {
            continue;
        }

        for (const field of baseFeeFields(fee)) {
            if (!(Number(values[field]) > 0)) {
                errors[field] = 'Must be greater than zero';
            }
        }
    }

    return errors;
};

export const payerStyle = (routing: unknown): PayerStyle => {
    if ('group' === routing) {
        return { icon: faStore, background: 'bg-sky-100', color: 'text-sky-600' };
    }

    if ('org_unit' === routing) {
        return { icon: faBuildingFlag, background: 'bg-violet-100', color: 'text-violet-600' };
    }

    return { icon: faCrown, background: 'bg-amber-100', color: 'text-amber-600' };
};

export const NOT_IN_USE_STYLE: PayerStyle = { icon: faBan, background: 'bg-gray-200', color: 'text-gray-500' };

export const formatMoney = (dollars: number): string => {
    const [integerPart = '', decimalPart = ''] = dollars.toFixed(5).split('.');

    return `$${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${decimalPart}`;
};

export const testId = (value: string): string => value.replaceAll('_', '-');
